<template>
  <ion-header>
    <ion-toolbar :style="contentStyles">
      <ion-buttons slot="start">
        <ion-back-button />
      </ion-buttons>
      <ion-title>
        <span>{{ selectedCategory?.title || $t('order.menus') }}</span>
      </ion-title>
    </ion-toolbar>
  </ion-header>
  <ion-content ref="scrollview" :style="contentStyles">
    <AppStack direction="flex-col" class="min-h-full">
      <AppStack
        direction="flex-col"
        flex="flex-1"
        justify-content="justify-end"
      >
        <OrderModalMenuItemsCategories
          v-if="selectedCategory === null"
          :categories="Object.values(categories)"
          :loading="loading"
          :order="order"
          class="w-full"
          @category-tap="onCategoryTap"
        />
        <OrderModalMenuItemsCategory
          v-else
          :menu-items="selectedCategory.menuItems"
          :loading="loading"
          :order="order"
          @menu-item-tap="onMenuItemTap"
          @configurator-opened="onConfiguratorOpened"
          @configurator-closed="onConfiguratorClosed"
        />
      </AppStack>
    </AppStack>
  </ion-content>
  <ion-footer>
    <div
      class="w-full bg-bg-elevation-2 border-border-elevation-2-primary border-x-0
        border-t-[0.5px] border-solid border-b-0 safe-bottom-max"
    >
      <AppStack direction="flex-row" class="px-4 pt-4 gap-2">
        <AppButton
          variant="secondary"
          @click="selectedCategoryId === null ? pop() : onBackTap()"
        >
          <IonIcon
            slot="start"
            :icon="arrowBack"
            size="medium"
            :class="[
              'scale-[1.3]',
              ...(selectedCategoryId
                ? ['text-fg-blue']
                : ['text-fg-elevation-0-primary']),
            ]"
          />
        </AppButton>
        <AppButton
          v-for="option in menuOptions"
          :key="option.value"
          :text="option.name"
          :variant="selectedMenuId === option.value ? 'primary' : 'secondary'"
          class="flex-1"
          @click="onSelectMenu(option.value)"
        >
          <template #default="{ class: classes }">
            <AppTypography
              :text="option.name"
              :class="[
                'relative whitespace-nowrap overflow-hidden text-ellipsis block',
                classes,
              ]"
              name="text-base-6-semibold"
            />
          </template>
        </AppButton>
      </AppStack>
    </div>
  </ion-footer>
</template>

<script lang="ts">
export default { name: 'OrderModalMenuItems' }
</script>

<script setup lang="ts">
import {
  IonButtons,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonFooter,
  IonBackButton,
  IonIcon,
} from '@ionic/vue'
import { arrowBack } from 'ionicons/icons'
import { ref, computed, inject, ComputedRef, onMounted } from 'vue'
import useAppEventBus from '~/composables/useAppEventBus'
import useBatchEventBus from '~/composables/useBatchEventBus'
import { useIonicNavigation } from '@restify/packages/composables/useIonicNavigation'
import useReactivePagination from '@restify/packages/composables/useReactivePagination'
import AppStack from '@restify/packages/design-system/low-level/AppStack.vue'
import AppTypography from '@restify/packages/design-system/low-level/AppTypography.vue'
import useAppHelpers from '~/composables/useAppHelpers'
import useStores, { type Stores } from '~/composables/useStores'
import { useAppStore } from '~/stores/app'
import AppButton from '~/components/AppButton.vue'
import AppHeaderTabs from '~/components/AppHeaderTabs.vue'
import OrderModalMenuItemsCategories from './OrderModalMenuItemsCategories.vue'
import OrderModalMenuItemsCategory from './OrderModalMenuItemsCategory.vue'
import { type ExtractEmits } from '@restify/packages/types'

const injectedOrderId = inject<ComputedRef<string>>('orderId')
const { pop } = useIonicNavigation('order-modal-nav')
const {
  orders: OrdersStore,
  'menu-items': MenuItemsStore,
  menus: MenusStore,
  storefronts: StorefrontsStore,
} = useStores()
const AppStore = useAppStore()
const appBus = useAppEventBus()
const batchBus = useBatchEventBus()
const { translateField } = useAppHelpers()

const scrollview = ref<InstanceType<typeof IonContent> | null>(null)

const loading = ref(false)
const selectedCategoryId = ref<string | null>(null)
const selectedMenuId = ref<string | null>(null)

const { find: findMenus } = useReactivePagination(
  MenusStore,
  `${AppStore.selectedStorefront._id}-catalog`,
  {
    status: 'public',
    $skip: 0,
    $limit: 500,
  },
  {
    store: MenuItemsStore,
    query: (menus) => {
      return {
        _id: {
          $in: [
            ...new Set([
              ...menus.reduce((acc: string[], menu) => {
                acc.push(
                  ...(menu.value?.menuItems?.map(
                    ({ menuItemId }) => menuItemId,
                  ) || []),
                )

                return acc
              }, []),
            ]),
          ],
        },
        $limit: 500,
      }
    },
  },
)

const menuOptions = computed(() => [
  ...menus.value.map((menu) => ({
    name: translateField(menu?.name || []),
    value: menu?._id,
  })),
])

const contentStyles = computed(() => ({
  '--background': 'var(--rf-bg-elevation-2-breaker)',
  '--border-color': 'var(--rf-border-elevation-0-primary)',
}))

const order = computed(() => {
  if (!injectedOrderId?.value) return null

  return OrdersStore.getFromStore(injectedOrderId.value).value || null
})

const storefront = computed(() => {
  if (!AppStore.selectedStorefrontId) return null

  return StorefrontsStore.getFromStore(AppStore.selectedStorefrontId).value
})

const menuIds = computed(() => {
  if (!storefront.value) return []

  return [...(storefront.value?.inHouseService?.menuIds || [])]
})

const menuItemIds = computed(() => {
  if (!storefront.value || !menuIds.value.length || !menus.value.length) {
    return []
  }

  return [
    ...new Set([
      ...menus.value.reduce((acc: string[], menu) => {
        acc.push(...menu.menuItems.map(({ menuItemId }) => menuItemId))

        return acc
      }, []),
    ]),
  ]
})

const selectedMenu = computed(() => {
  if (!selectedMenuId.value) return null

  return menus.value.find((menu) => menu._id === selectedMenuId.value)
})

const menus = computed(() => {
  if (!menuIds.value.length) return []

  return MenusStore.findInStore({
    _id: { $in: menuIds.value },
    status: 'public',
  })
    .map((item) => item.value)
    .filter((item) => item !== undefined)
})

const categories = computed(() => {
  const menu = selectedMenu.value

  if (!menu) return {}

  const mappedCategories = menu.categories.reduce(
    (
      acc: Record<
        string,
        {
          id: string
          title: string
          menuItems: Stores['menu-items']['Result'][]
        }
      >,
      category,
    ) => {
      acc[category.id] = {
        id: category.id,
        title: translateField(category.name),
        menuItems: [],
      }

      return acc
    },
    {},
  )

  menu.menuItems.forEach((item) => {
    const menuItem = menuItems.value[item.menuItemId]
    const category = mappedCategories[item.categoryId]

    if (menuItem && category) {
      category.menuItems.push(menuItem)
    }
  })

  return mappedCategories
})

const selectedCategory = computed(() => {
  return selectedCategoryId.value
    ? categories.value[selectedCategoryId.value]
    : null
})

const menuItems = computed(() => {
  if (!menuItemIds.value.length) return {}

  return MenuItemsStore.findInStore({
    _id: { $in: menuItemIds.value },
    $limit: 500,
  })
    .map((item) => item.value)
    .filter((item) => item !== undefined)
    .reduce((acc: Record<string, Stores['menu-items']['Result']>, item) => {
      acc[item._id] = item

      return acc
    }, {})
})

const onSelectMenu: ExtractEmits<typeof AppHeaderTabs>['update:active-tab'] = (
  value,
) => {
  selectedCategoryId.value = null
  selectedMenuId.value = value
}

const onCategoryTap = (categoryId: string) => {
  selectedCategoryId.value = categoryId
}

const onBackTap = () => {
  selectedCategoryId.value = null
}

const onMenuItemTap: ExtractEmits<
  typeof OrderModalMenuItemsCategory
>['menu-item-tap'] = (payload) => {
  batchBus.emit('batch-add', payload)

  payload.onClick?.()
}

const init = (silent = false, force = false) => {
  loading.value = silent ? false : true

  return findMenus(force)
    .then(() => {
      selectedMenuId.value = menus.value[0]?._id || null
    })
    .finally(() => (loading.value = false))
}

const onConfiguratorOpened: ExtractEmits<
  typeof OrderModalMenuItemsCategory
>['configurator-opened'] = ($event) => {
  $event?.scrollIntoView({ behavior: 'smooth' })
}

const onConfiguratorClosed = () => {
  scrollview.value?.$el?.scrollToTop(200)
}

appBus.on((event) => {
  if (event.type === 'app-visible') {
    return init()
  }
})

onMounted(() => {
  return init()
})
</script>
